import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useTranslations } from "@/hooks/useTranslations";
import { useLanguage } from "@/components/LanguageContext";
import { Button } from "@/components/ui/button";
import { Copy, Share2, Check, X, Edit } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useDebouncedCallback } from "use-debounce";
import { useAuth } from "@/components/AuthProvider";

interface ProfileUrlInputProps {
  value: string;
  onChange: (value: string) => void;
}

export const ProfileUrlInput = ({ value, onChange }: ProfileUrlInputProps) => {
  const { t } = useTranslations("profile");
  const { language } = useLanguage();
  const { toast } = useToast();
  const { profile } = useAuth();
  const [isChecking, setIsChecking] = useState(false);
  const [isAvailable, setIsAvailable] = useState<boolean | null>(null);
  const [isSameAsCurrentUrl, setIsSameAsCurrentUrl] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  
  const prefix = language === 'pt' ? "trainerfolio.com/pt/pp/" : "trainerfolio.com/pp/";
  
  const fullUrl = `https://${prefix}${value}`;

  useEffect(() => {
    if (profile && profile.public_url === value) {
      setIsSameAsCurrentUrl(true);
      setIsAvailable(null);
      
      if (!isEditing) {
        setIsEditing(false);
      }
    } else {
      setIsSameAsCurrentUrl(false);
    }
  }, [value, profile, isEditing]);

  const checkUrlAvailability = useDebouncedCallback(async (url: string) => {
    if (!url || url.length < 3) {
      setIsAvailable(null);
      return;
    }

    if (isSameAsCurrentUrl && !isEditing) {
      setIsAvailable(null);
      return;
    }

    setIsChecking(true);
    try {
      const { data, error } = await supabase
        .rpc('check_public_url_available', { url_to_check: url });
      
      if (error) throw error;
      setIsAvailable(data);
    } catch (error) {
      console.error("Error checking URL availability:", error);
      setIsAvailable(null);
    } finally {
      setIsChecking(false);
    }
  }, 500);

  useEffect(() => {
    if (isEditing || !isSameAsCurrentUrl) {
      checkUrlAvailability(value);
    }
  }, [value, checkUrlAvailability, isSameAsCurrentUrl, isEditing]);
  
  const handleCopy = () => {
    if (!value) {
      toast({
        title: t("previewError"),
        description: t("previewErrorDesc"),
        variant: "destructive",
      });
      return;
    }
    
    navigator.clipboard.writeText(fullUrl).then(() => {
      toast({
        title: language === 'pt' ? "Copiado!" : "Copied!",
        description: language === 'pt' 
          ? "URL copiado para a área de transferência" 
          : "URL copied to clipboard",
      });
    });
  };
  
  const handleShare = async () => {
    if (!value) {
      toast({
        title: t("previewError"),
        description: t("previewErrorDesc"),
        variant: "destructive",
      });
      return;
    }
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: language === 'pt' ? "Meu perfil de treinador" : "My trainer profile",
          url: fullUrl,
        });
      } catch (error) {
        console.error("Error sharing:", error);
      }
    } else {
      handleCopy();
      toast({
        title: language === 'pt' ? "Partilha não suportada" : "Sharing not supported",
        description: language === 'pt' 
          ? "URL copiado para a área de transferência" 
          : "URL copied to clipboard",
      });
    }
  };

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const getBorderColor = () => {
    if (isSameAsCurrentUrl && !isEditing) return "";
    if (isAvailable === null) return "";
    if (isAvailable) return "border-green-500 dark:border-green-600";
    return "border-red-500 dark:border-red-600";
  };
  
  return (
    <div className="space-y-2">
      <Label htmlFor="publicUrl" className="block text-slate-800 dark:text-gray-200">
        {t("publicUrl")}
      </Label>
      <div className="flex flex-col space-y-2">
        <div className="flex flex-col md:flex-row md:items-center">
          <div className="w-full md:w-auto bg-slate-100 dark:bg-gray-700 px-3 py-2 text-slate-500 dark:text-gray-300 text-sm rounded-t-md md:rounded-t-none md:rounded-l-md border border-b-0 md:border-b md:border-r-0 border-slate-200 dark:border-gray-600">
            {prefix}
          </div>
          <div className="relative flex-1 w-full">
            <Input
              id="publicUrl"
              type="text"
              className={`w-full rounded-t-none md:rounded-t-md rounded-b-md md:rounded-l-none ${isSameAsCurrentUrl && !isEditing ? 'bg-slate-50 dark:bg-gray-800' : 'bg-white dark:bg-gray-700'} border-slate-200 dark:border-gray-600 text-slate-900 dark:text-gray-100 pr-10 ${getBorderColor()}`}
              placeholder={t("publicUrlPlaceholder")}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              readOnly={isSameAsCurrentUrl && !isEditing}
              disabled={isSameAsCurrentUrl && !isEditing}
            />
            {isSameAsCurrentUrl && !isEditing ? (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-2 top-1/2 transform -translate-y-1/2"
                onClick={handleEditClick}
                title={t("editUrl")}
              >
                <Edit className="h-4 w-4 text-slate-600 dark:text-gray-400" />
              </Button>
            ) : (
              value && value.length >= 3 && !isSameAsCurrentUrl && (
                <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                  {isChecking ? (
                    <div className="h-5 w-5 border-2 border-t-transparent border-slate-500 dark:border-gray-400 rounded-full animate-spin" />
                  ) : isAvailable === true ? (
                    <Check className="h-5 w-5 text-green-500 dark:text-green-400" />
                  ) : isAvailable === false ? (
                    <X className="h-5 w-5 text-red-500 dark:text-red-400" />
                  ) : null}
                </div>
              )
            )}
          </div>
        </div>
        <div className="flex gap-2 w-full">
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="flex-1 bg-white dark:bg-gray-700 border-slate-200 dark:border-gray-600 text-slate-700 dark:text-gray-300"
            onClick={handleCopy}
            title={language === 'pt' ? "Copiar URL" : "Copy URL"}
          >
            <Copy className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="flex-1 bg-white dark:bg-gray-700 border-slate-200 dark:border-gray-600 text-slate-700 dark:text-gray-300"
            onClick={handleShare}
            title={language === 'pt' ? "Partilhar URL" : "Share URL"}
          >
            <Share2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <p className="text-xs text-slate-500 dark:text-gray-400">
        {t("publicUrlHelp")}
      </p>
      {isAvailable === false && !isSameAsCurrentUrl && (
        <p className="text-xs text-red-500 dark:text-red-400 mt-1">
          {t("urlNotAvailable")}
        </p>
      )}
      {isAvailable === true && (
        <p className="text-xs text-green-500 dark:text-green-400 mt-1">
          {t("urlAvailable")}
        </p>
      )}
    </div>
  );
};
